import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { PlusIcon, SparkleIcon } from "@phosphor-icons/react";
import type { Skill } from "@reactive-resume/schema";

import { SkillCard } from "./skill-card";

type Props = {
  items: Skill[];
  onAdd: () => void;
  onEdit: (item: Skill) => void;
  onDelete: (id: string) => void;
  onReorder: (active: string, over: string) => void;
};
export const SkillList = ({ items, onAdd, onEdit, onDelete, onReorder }: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const dragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  };
  if (!items.length)
    return (
      <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/30 px-5 py-9 text-center">
        <span className="mx-auto grid size-11 place-items-center rounded-xl bg-white text-indigo-600 shadow-sm">
          <SparkleIcon size={22} weight="duotone" />
        </span>
        <h3 className="mt-3 text-sm font-bold text-indigo-950">Aucune compétence ajoutée</h3>
        <p className="mx-auto mt-1 max-w-sm text-[11px] leading-5 text-slate-500">
          Ajoutez les savoir-faire les plus importants pour le poste ciblé.
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800"
        >
          <PlusIcon size={15} weight="bold" /> Ajouter une compétence
        </button>
      </div>
    );
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragEnd={dragEnd}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <div className="grid gap-2.5 sm:grid-cols-2">
          {items.map((item) => (
            <SkillCard key={item.id} item={item} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
